/**
 * Installments Management Page
 * Owner page for managing installment plans and payment schedules
 *
 * Every plan is loaded once and filtered and paged here: the API takes
 * only a status, and pages by offset rather than the `page` this page
 * used to send, so page 2 repeated page 1.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";
import DataTable from "../../components/admin/DataTable";
import FilterBar from "../../components/admin/FilterBar";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import installmentsService from "../../api/services/installments.service";
import Header from "../../components/Header";

// Today as YYYY-MM-DD in local time, to compare with date-only due dates
const localToday = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const isOverdue = (plan) =>
  plan.status === "active" &&
  Boolean(plan.next_due_date) &&
  plan.next_due_date < localToday();

export default function Installments() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal states
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    action: null,
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await installmentsService.getAll({ limit: 500 });
      setPlans(Array.isArray(response) ? response : response?.items || []);
    } catch (error) {
      console.error("Failed to fetch installment plans:", error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch installment plans
  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Changing a filter must return to page 1, or a later page of the old
  // result is shown and reads as "nothing found".
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery, overdueOnly]);

  const filteredPlans = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    return plans.filter((plan) => {
      if (statusFilter === "overdue") {
        if (!isOverdue(plan)) return false;
      } else if (statusFilter && plan.status !== statusFilter) {
        return false;
      }
      if (overdueOnly && !isOverdue(plan)) return false;
      if (!needle) return true;
      return [
        plan.family_name,
        plan.family_email,
        plan.child_names,
        plan.class_names,
      ].some((field) => field?.toLowerCase().includes(needle));
    });
  }, [plans, statusFilter, searchQuery, overdueOnly]);

  const closeDialog = () =>
    setConfirmDialog((dialog) => ({ ...dialog, isOpen: false }));

  // Mark the plan's next payment as paid (received outside Stripe)
  const handleMarkAsPaid = async (paymentId) => {
    setIsProcessing(true);
    try {
      await installmentsService.markAsPaid(paymentId);
      alert("Payment marked as paid successfully");
      fetchPlans();
      closeDialog();
    } catch (error) {
      console.error("Failed to mark payment as paid:", error);
      alert("Failed to mark payment as paid");
    } finally {
      setIsProcessing(false);
    }
  };

  // Cancel installment plan
  const handleCancelPlan = async (planId) => {
    setIsProcessing(true);
    try {
      await installmentsService.cancelAsOwner(planId);
      alert("Installment plan cancelled successfully");
      fetchPlans();
      closeDialog();
    } catch (error) {
      console.error("Failed to cancel plan:", error);
      alert("Failed to cancel installment plan");
    } finally {
      setIsProcessing(false);
    }
  };

  // Define table columns
  const columns = [
    {
      key: "id",
      label: "Plan ID",
      sortable: true,
      render: (value) => <span className="font-mono text-sm">#{value}</span>,
    },
    {
      key: "family_name",
      label: "Customer",
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-semibold text-[#173151]">{value || "-"}</p>
          <p className="text-xs text-gray-500">{row.family_email}</p>
        </div>
      ),
    },
    {
      key: "child_names",
      label: "Child",
      render: (value) => <span className="text-gray-700">{value || "-"}</span>,
    },
    {
      key: "class_names",
      label: "Class",
      render: (value) => <span className="text-gray-700">{value || "-"}</span>,
    },
    {
      key: "total_amount",
      label: "Total Amount",
      type: "currency",
      sortable: true,
      align: "right",
    },
    {
      key: "paid_count",
      label: "Payments",
      render: (value, row) => (
        <div className="text-sm">
          <span className="font-semibold text-[#173151]">
            {row.paid_count || 0}/{row.total_count || 0}
          </span>
          <p className="text-xs text-gray-500">
            ${parseFloat(row.amount_paid || 0).toFixed(2)} paid
          </p>
        </div>
      ),
    },
    {
      key: "next_due_date",
      label: "Next Due",
      render: (value, row) => {
        if (!value || row.status === "completed") {
          return <span className="text-xs text-gray-500">-</span>;
        }

        const [year, month, day] = value.split("-").map(Number);
        const dueDate = new Date(year, month - 1, day);
        const overdue = isOverdue(row);

        return (
          <div
            className={`text-sm ${overdue ? "text-red-600" : "text-gray-700"}`}
          >
            <div className="flex items-center gap-1">
              {overdue && <AlertCircle className="w-4 h-4" />}
              <span>
                {dueDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
            {overdue && (
              <span className="text-xs font-semibold">OVERDUE</span>
            )}
          </div>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      type: "status",
      sortable: true,
    },
    {
      key: "actions",
      label: "Actions",
      type: "actions",
      align: "right",
      actions: (row) => [
        {
          label: "Mark Next as Paid",
          icon: CheckCircle,
          onClick: () => {
            setConfirmDialog({
              isOpen: true,
              title: "Mark Payment as Paid",
              message:
                "Are you sure you want to mark the next installment payment as paid? This cannot be undone.",
              action: () => handleMarkAsPaid(row.next_payment_id),
            });
          },
          disabled:
            !row.next_payment_id ||
            row.status === "completed" ||
            row.status === "cancelled",
        },
        {
          label: "Cancel Plan",
          icon: XCircle,
          variant: "destructive",
          onClick: () => {
            setConfirmDialog({
              isOpen: true,
              title: "Cancel Installment Plan",
              message: `Are you sure you want to cancel this installment plan? This action cannot be undone.`,
              action: () => handleCancelPlan(row.id),
            });
          },
          disabled: row.status === "completed" || row.status === "cancelled",
        },
      ],
    },
  ];

  // Filter configuration
  const filters = [
    {
      type: "select",
      placeholder: "All Statuses",
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: "active", label: "Active" },
        { value: "completed", label: "Completed" },
        { value: "cancelled", label: "Cancelled" },
        { value: "overdue", label: "Overdue" },
      ],
    },
  ];

  const hasActiveFilters = statusFilter || searchQuery || overdueOnly;

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setOverdueOnly(false);
    setCurrentPage(1);
  };

  // Calculate stats
  const overduePlans = plans.filter(isOverdue).length;

  const totalActive = plans.filter((p) => p.status === "active").length;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <Header />

      <div className="max-w-9xl mx-auto px-3 sm:px-4 py-4 flex-1 flex flex-col min-h-0 w-full">
        <div className="shrink-0 mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center items-start gap-3 sm:gap-4 justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-[30px] lg:text-[46px] font-bold text-text-primary font-kollektif truncate">
              Installments Management
            </h1>
            <p className="text-xs sm:text-sm text-neutral-main font-manrope mt-1 hidden sm:block">
              Manage payment plans and schedules
            </p>
          </div>

          <div className="flex gap-2 sm:gap-4 shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 p-2 sm:p-4 min-w-[80px] sm:min-w-[120px]">
              <p className="text-xs sm:text-sm text-gray-600 font-manrope">
                Active Plans
              </p>
              <p className="text-lg sm:text-2xl font-bold text-[#173151] font-manrope mt-1">
                {totalActive}
              </p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-2 sm:p-4 min-w-[80px] sm:min-w-[120px]">
              <div className="flex items-center gap-1 sm:gap-2">
                <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />
                <p className="text-xs sm:text-sm text-gray-600 font-manrope">
                  Overdue
                </p>
              </div>
              <p className="text-lg sm:text-2xl font-bold text-red-600 font-manrope mt-1">
                {overduePlans}
              </p>
            </div>
          </div>
        </div>

        <div className="shrink-0">
          <FilterBar
            searchValue={searchQuery}
            searchPlaceholder="Search by customer name, child, or class..."
            onSearch={setSearchQuery}
            filters={filters}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
          />
        </div>

        <div className="shrink-0 mb-2 sm:mb-4">
          <label className="flex items-center gap-2 cursor-pointer w-fit">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => setOverdueOnly(e.target.checked)}
              className="w-4 h-4 text-[#F3BC48] focus:ring-[#F3BC48] rounded"
            />
            <span className="text-xs sm:text-sm font-manrope text-gray-700 font-semibold">
              Show Overdue Only
            </span>
          </label>
        </div>

        <div className="flex-1 min-h-0 flex flex-col pb-2">
          <DataTable
            columns={columns}
            data={filteredPlans}
            loading={loading}
            emptyMessage="No installment plans found"
            pagination={true}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalItems={filteredPlans.length}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant="warning"
        isLoading={isProcessing}
      />
    </div>
  );
}
